import { DeleteOutlined, EditOutlined, PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import type { TourProps } from 'antd';
import { Button, Col, ConfigProvider, DatePicker, Form, Image, Input, InputNumber, Modal, notification, Popconfirm, Row, Select, Space, Table, Tag, Tour } from 'antd';
import viVN from 'antd/locale/vi_VN';
import dayjs from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import useDrivePicker from 'react-google-drive-picker';
import AdminLayout from '../components/AdminLayout';
import { GrinderService } from '../services/grinderService';
import '../styles/AdminPages.css';
import { CoffeeGrinder } from '../types/grinder';

const { Search } = Input;

const grinderService = new GrinderService();

const GrinderManagement: React.FC = () => {
    const [grinders, setGrinders] = useState<CoffeeGrinder[]>([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingGrinder, setEditingGrinder] = useState<CoffeeGrinder | null>(null);
    const [form] = Form.useForm();
    const [searchText, setSearchText] = useState('');
    const [filteredGrinders, setFilteredGrinders] = useState<CoffeeGrinder[]>([]);
    const [open, setOpen] = useState<boolean>(false);
    const [imageFiles, setImageFiles] = useState<File[]>([]);
    const [imageFilesPreview, setImageFilesPreview] = useState<any[]>([]);
    const [driveImages, setDriveImages] = useState<{ fileId: string; webViewLink: string; fileName: string; }[]>([]);
    const [driveImagesPreview, setDriveImagesPreview] = useState<{ fileId: string; webViewLink: string; fileName: string; }[]>([]);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [submitLoading, setSubmitLoading] = useState(false);

    const titleRef = useRef<HTMLDivElement>(null);
    const addButtonRef = useRef<HTMLButtonElement>(null);
    const searchRef = useRef<any>(null);
    const tableRef = useRef<HTMLDivElement>(null);

    const [openPicker] = useDrivePicker();

    useEffect(() => {
        fetchGrinders();
    }, []);

      useEffect(() => {
    const filtered = grinders.filter(grinder =>
      grinder.product_name.toLowerCase().includes(searchText.toLowerCase()) ||
      grinder.info.toLowerCase().includes(searchText.toLowerCase()) ||
      grinder.material.toLowerCase().includes(searchText.toLowerCase()) ||
      grinder.color.toLowerCase().includes(searchText.toLowerCase()) ||
      grinder.manufacture.toLowerCase().includes(searchText.toLowerCase())
    );
    setFilteredGrinders(filtered);
  }, [grinders, searchText]);

    const fetchGrinders = async () => {
        setLoading(true);
        try {
            console.log('Fetching grinders...');
            const data = await grinderService.getAll();
            console.log('Grinders fetched:', data);
            setGrinders(data);
            setFilteredGrinders(data);
        } catch (error) {
            console.error('Error fetching grinders:', error);
            notification.error({
                message: 'Lỗi',
                description: 'Không thể tải danh sách máy xay cà phê',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        setEditingGrinder(null);
        form.resetFields();
        setImageFiles([]);
        setImageFilesPreview([]);
        setDriveImages([]);
        setDriveImagesPreview([]);
        setUploadProgress(0);
        setIsModalVisible(true);
    };

    const handleEdit = (grinder: CoffeeGrinder) => {
        setEditingGrinder(grinder);

        // Reset image states
        setImageFiles([]);
        setDriveImages([]);
        setUploadProgress(0);

        // Set existing images for preview
        if (grinder.driveImages) {
            setDriveImagesPreview(grinder.driveImages.map(img => ({
                ...img,
                fileName: img.fileName || 'Untitled'
            })));
        } else {
            setDriveImagesPreview([]);
        }

        if (grinder.images) {
            setImageFilesPreview(grinder.images);
        } else {
            setImageFilesPreview([]);
        }

        // Convert date strings to dayjs objects for form
        const formData = {
            ...grinder,
            purchase_date: grinder.purchase_date ? dayjs(grinder.purchase_date) : undefined,
            warranty_expiry: grinder.warranty_expiry ? dayjs(grinder.warranty_expiry) : undefined,
        };

        form.setFieldsValue(formData);
        setIsModalVisible(true);
    };

    const handleDelete = async (id: string) => {
        try {
            await grinderService.delete(id);
            fetchGrinders(); // Refresh danh sách
            notification.success({
                message: 'Thành công',
                description: 'Đã xóa máy xay cà phê',
            });
        } catch (error) {
            notification.error({
                message: 'Lỗi',
                description: 'Không thể xóa máy xay cà phê',
            });
        }
    };

    // Hàm xử lý chọn ảnh từ local
    const handleImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        setImageFiles([...imageFiles, ...files]);
    };

    // Hàm xử lý chọn ảnh từ Google Drive
    const handleOpenPicker = () => {
        openPicker({
            clientId: "85231195954-hsetf3cpqpfcr67p2k40001og3054t52.apps.googleusercontent.com",
            developerKey: "AIzaSyD-xnVTuCx7ZDPzxrTmeL4my9FuIfkpYtY",
            viewId: "DOCS",
            showUploadView: true,
            showUploadFolders: true,
            supportDrives: true,
            multiselect: false,
            callbackFunction: (data) => {
                if (data.action === 'picked') {
                    const file = data.docs[0];
                    setDriveImages(prev => [...prev, {
                        fileId: file.id,
                        webViewLink: file.url || '',
                        fileName: file.name
                    }]);
                }
            },
        });
    };

    // Hàm xóa ảnh local
    const handleRemoveLocalImage = (index: number) => {
        setImageFiles(prev => prev.filter((_, i) => i !== index));
    };

    // Hàm xóa ảnh preview local
    const handleRemoveLocalPreviewImage = (index: number) => {
        setImageFilesPreview(prev => prev.filter((_, i) => i !== index));
    };

    // Hàm xóa ảnh Drive
    const handleRemoveDriveImage = (index: number) => {
        setDriveImages(prev => prev.filter((_, i) => i !== index));
    };

    // Hàm xóa ảnh preview Drive
    const handleRemoveDrivePreviewImage = (index: number) => {
        setDriveImagesPreview(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (values: any) => {
        try {
            setSubmitLoading(true);
            setUploadProgress(0);

            // Kiểm tra hình ảnh (không bắt buộc, chỉ kiểm tra nếu cần)
            // if (imageFiles.length === 0 && driveImages.length === 0 &&
            //     (!editingGrinder || (!editingGrinder.images?.length && !editingGrinder.driveImages?.length))) {
            //     notification.error({
            //         message: 'Lỗi',
            //         description: 'Vui lòng chọn ít nhất một hình ảnh',
            //     });
            //     setSubmitLoading(false);
            //     return;
            // }

            const lstDriveImages: any[] = [];
            const newsLstImage: any[] = [];
            const oldLstImage: any[] = [];

            imageFiles.forEach(file => {
                newsLstImage.push(file);
            });

            imageFilesPreview?.forEach(file => {
                oldLstImage.push(file);
            });

            driveImages.forEach(image => {
                lstDriveImages.push({
                    fileId: image.fileId,
                    webViewLink: image.webViewLink,
                    fileName: image.fileName
                });
            });

            driveImagesPreview?.forEach(image => {
                lstDriveImages.push({
                    fileId: image.fileId,
                    webViewLink: image.webViewLink,
                    fileName: image.fileName
                });
            });

            // Hiển thị progress indicator nếu có ảnh cần upload
            if (newsLstImage.length > 0) {
                setUploadProgress(10);
                // Simulate progress
                const interval = setInterval(() => {
                    setUploadProgress(prev => {
                        if (prev >= 90) {
                            clearInterval(interval);
                            return 90;
                        }
                        return prev + 5;
                    });
                }, 1000);
            }

            // Convert dayjs objects to date strings
            const formattedValues = {
                ...values,
                purchase_date: values.purchase_date ? values.purchase_date.format('YYYY-MM-DD') : undefined,
                warranty_expiry: values.warranty_expiry ? values.warranty_expiry.format('YYYY-MM-DD') : undefined,
                driveImages: lstDriveImages,
            };

            if (editingGrinder) {
                await grinderService.updateGrinder(
                    editingGrinder.id!,
                    formattedValues,
                    newsLstImage,
                    oldLstImage
                );
                notification.success({
                    message: 'Thành công',
                    description: 'Đã cập nhật máy xay cà phê',
                });
            } else {
                await grinderService.addGrinder(formattedValues, imageFiles);
                notification.success({
                    message: 'Thành công',
                    description: 'Đã thêm máy xay cà phê mới',
                });
            }

            setUploadProgress(100);
            setIsModalVisible(false);
            form.resetFields();
            setImageFiles([]);
            setImageFilesPreview([]);
            setDriveImages([]);
            setDriveImagesPreview([]);
            fetchGrinders(); // Refresh danh sách
        } catch (error) {
            notification.error({
                message: 'Lỗi',
                description: 'Không thể lưu thông tin máy xay cà phê',
            });
        } finally {
            setSubmitLoading(false);
            setTimeout(() => setUploadProgress(0), 1000);
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'active': return 'green';
            case 'inactive': return 'red';
            case 'maintenance': return 'orange';
            default: return 'default';
        }
    };

    const getStatusText = (status: string) => {
        switch (status) {
            case 'active': return 'Hoạt động';
            case 'inactive': return 'Ngừng hoạt động';
            case 'maintenance': return 'Bảo trì';
            default: return status;
        }
    };

    const columns = [
        {
            title: '#',
            dataIndex: 'index',
            key: 'index',
            width: 60,
            render: (_: any, __: any, index: number) => index + 1
        },
        {
            title: 'Hình ảnh',
            dataIndex: 'images',
            key: 'images',
            width: 120,
            render: (images: string[], record: CoffeeGrinder) => (
                <Image.PreviewGroup>
                    <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 8,
                        maxWidth: 120,
                    }}>
                        {(record.driveImages && record.driveImages.length > 0) && record.driveImages.map((image, index) => (
                            <div key={index} style={{ marginBottom: 8 }}>
                                <Image
                                    src={`https://lh3.googleusercontent.com/d/${image.fileId}?authuser=server`}
                                    alt={image.fileName}
                                    style={{ width: 50, height: 50, objectFit: 'cover' }}
                                />
                            </div>
                        ))}

                        {images && images.map((url, index) => (
                            <div key={index} style={{ marginBottom: 8 }}>
                                <Image
                                    src={url}
                                    alt={`grinder-${index}`}
                                    width={50}
                                    height={50}
                                    style={{ objectFit: 'cover' }}
                                />
                            </div>
                        ))}
                    </div>
                </Image.PreviewGroup>
            )
        },
        {
            title: 'Tên sản phẩm',
            dataIndex: 'product_name',
            key: 'product_name',
            width: 250,
            sorter: (a: CoffeeGrinder, b: CoffeeGrinder) => a.product_name.localeCompare(b.product_name),
            ellipsis: true,
        },
        {
            title: 'Thông tin',
            dataIndex: 'info',
            key: 'info',
            width: 200,
            ellipsis: true,
            render: (info: string) => (
                <span title={info}>
                    {info.length > 50 ? `${info.substring(0, 50)}...` : info}
                </span>
            )
        },
        {
            title: 'Khối lượng',
            dataIndex: 'weight',
            key: 'weight',
            width: 100,
        },
        {
            title: 'Kích thước',
            dataIndex: 'size_diameter',
            key: 'size_diameter',
            width: 120,
            render: (diameter: string, record: CoffeeGrinder) => 
                `${diameter} x ${record.size_height}`
        },
        {
            title: 'Pin',
            dataIndex: 'battery_capacity',
            key: 'battery_capacity',
            width: 100,
        },
        {
            title: 'Dung tích',
            dataIndex: 'max_capacity',
            key: 'max_capacity',
            width: 100,
        },
        {
            title: 'Cấp độ xay',
            dataIndex: 'grind_setting_count',
            key: 'grind_setting_count',
            width: 100,
        },
        {
            title: 'Lưỡi xay',
            dataIndex: 'burr_diameter',
            key: 'burr_diameter',
            width: 100,
        },
        {
            title: 'Vật liệu',
            dataIndex: 'material',
            key: 'material',
            width: 150,
            ellipsis: true,
        },
        {
            title: 'Thời gian sạc',
            dataIndex: 'charging_time',
            key: 'charging_time',
            width: 100,
        },
        {
            title: 'Màu sắc',
            dataIndex: 'color',
            key: 'color',
            width: 120,
            ellipsis: true,
        },
        {
            title: 'Tính năng',
            dataIndex: 'special_feature',
            key: 'special_feature',
            width: 150,
            ellipsis: true,
            render: (feature: string) => (
                <span title={feature}>
                    {feature.length > 30 ? `${feature.substring(0, 30)}...` : feature}
                </span>
            )
        },
        {
            title: 'Hãng SX',
            dataIndex: 'manufacture',
            key: 'manufacture',
            width: 100,
        },
        {
            title: 'Giá (VND)',
            dataIndex: 'price',
            key: 'price',
            width: 120,
            sorter: (a: CoffeeGrinder, b: CoffeeGrinder) => a.price - b.price,
            render: (price: number) => `${price.toLocaleString('vi-VN')} ₫`,
        },
        {
            title: 'Vị trí',
            dataIndex: 'location',
            key: 'location',
            width: 120,
            render: (location: string) => location || '-'
        },
        {
            title: 'Ngày mua',
            dataIndex: 'purchase_date',
            key: 'purchase_date',
            width: 120,
            render: (date: string) => date ? dayjs(date).format('DD/MM/YYYY') : '-'
        },
        {
            title: 'Hết hạn BH',
            dataIndex: 'warranty_expiry',
            key: 'warranty_expiry',
            width: 120,
            render: (date: string) => date ? dayjs(date).format('DD/MM/YYYY') : '-'
        },
        {
            title: 'Trạng thái',
            dataIndex: 'status',
            key: 'status',
            width: 120,
            render: (status: string) => (
                <Tag color={getStatusColor(status)}>
                    {getStatusText(status)}
                </Tag>
            ),
        },
        {
            title: 'Thao tác',
            key: 'action',
            fixed: 'right' as const,
            width: 120,
            render: (_: any, record: CoffeeGrinder) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    />
                    <Popconfirm
                        title="Xóa máy xay cà phê"
                        description="Bạn có chắc chắn muốn xóa máy xay này?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Xóa"
                        cancelText="Hủy"
                        okButtonProps={{ danger: true }}
                    >
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const steps: TourProps['steps'] = [
        {
            title: 'Chào mừng đến với Quản lý máy xay cà phê!',
            description: 'Đây là nơi bạn có thể quản lý tất cả các máy xay cà phê trong cửa hàng.',
            target: () => titleRef.current!,
        },
        {
            title: 'Thêm máy xay mới',
            description: 'Nhấp vào nút này để thêm máy xay cà phê mới vào hệ thống.',
            target: () => addButtonRef.current!,
        },
        {
            title: 'Tìm kiếm máy xay',
            description: 'Sử dụng ô tìm kiếm này để tìm máy xay theo tên, thông tin, vật liệu hoặc màu sắc.',
            target: () => searchRef.current!,
        },
        {
            title: 'Danh sách máy xay',
            description: 'Bảng này hiển thị tất cả máy xay cà phê với thông tin chi tiết và các thao tác.',
            target: () => tableRef.current!,
        },
        {
            title: 'Kết thúc hướng dẫn!',
            description: 'Bây giờ bạn đã sẵn sàng quản lý máy xay cà phê một cách hiệu quả!',
            target: () => titleRef.current!,
        },
    ];

    return (
        <ConfigProvider locale={viVN}>
            <AdminLayout>
                <div className="admin-page-header">
                    <div className="admin-page-title">
                        <h1 ref={titleRef}>
                            Quản lý máy xay cà phê
                        </h1>
                        <Tag color="blue">{grinders.length} máy xay</Tag>
                        <Button
                            type="primary"
                            icon={<QuestionCircleOutlined />}
                            onClick={() => setOpen(true)}
                            style={{ marginLeft: 8 }}
                        >
                            Hướng dẫn
                        </Button>
                    </div>

                    <div className="admin-page-actions">
                        <div className="admin-search-filters">
                            <div ref={searchRef}>
                                <Search
                                    placeholder="Tìm kiếm theo tên, thông tin, vật liệu, màu sắc, nhà sản xuất..."
                                    allowClear
                                    style={{ width: 300 }}
                                    onChange={(e) => setSearchText(e.target.value)}
                                />
                            </div>
                        </div>

                        <Space>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={handleAdd}
                                className="admin-button"
                                ref={addButtonRef}
                            >
                                Thêm máy xay
                            </Button>
                        </Space>
                    </div>
                </div>

                <div className="admin-table-container" ref={tableRef}>
                    <Table
                        className="admin-table"
                        columns={columns}
                        dataSource={filteredGrinders}
                        rowKey="id"
                        loading={loading}
                        scroll={{ x: 2800 }}
                        pagination={{
                            responsive: true,
                            defaultPageSize: 10,
                            position: ['bottomRight'],
                            total: filteredGrinders.length,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) =>
                                `${range[0]}-${range[1]} của ${total} máy xay`,
                        }}
                    />
                </div>

                <Modal
                    title={editingGrinder ? 'Sửa máy xay cà phê' : 'Thêm máy xay cà phê'}
                    open={isModalVisible}
                    onCancel={() => setIsModalVisible(false)}
                    footer={null}
                    width={800}
                    className="admin-modal"
                    destroyOnClose={true}
                >
                    <div className="admin-form-container">
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={handleSubmit}
                        >
                            <Form.Item
                                name="product_name"
                                label="Tên sản phẩm"
                                rules={[{ required: true, message: 'Vui lòng nhập tên sản phẩm' }]}
                            >
                                <Input placeholder="Ví dụ: Cối xay điện OutIn Fino Portable Electric Espresso Coffee Grinder" />
                            </Form.Item>

                            <Form.Item
                                name="info"
                                label="Thông tin chi tiết"
                                rules={[{ required: true, message: 'Vui lòng nhập thông tin chi tiết' }]}
                            >
                                <Input.TextArea
                                    rows={4}
                                    placeholder="Thông tin chi tiết về sản phẩm, tính năng, ưu điểm..."
                                />
                            </Form.Item>

                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item
                                        name="weight"
                                        label="Khối lượng (g)"
                                        rules={[{ required: true, message: 'Vui lòng nhập khối lượng' }]}
                                    >
                                        <Input placeholder="Ví dụ: 690g" />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item
                                        name="size_diameter"
                                        label="Đường kính (mm)"
                                        rules={[{ required: true, message: 'Vui lòng nhập đường kính' }]}
                                    >
                                        <Input placeholder="Ví dụ: 73mm" />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item
                                        name="size_height"
                                        label="Chiều cao (mm)"
                                        rules={[{ required: true, message: 'Vui lòng nhập chiều cao' }]}
                                    >
                                        <Input placeholder="Ví dụ: 199mm" />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item
                                        name="battery_capacity"
                                        label="Dung lượng pin (mAh)"
                                        rules={[{ required: true, message: 'Vui lòng nhập dung lượng pin' }]}
                                    >
                                        <Input placeholder="Ví dụ: 1000mah" />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item
                                        name="max_capacity"
                                        label="Dung tích tối đa (g)"
                                        rules={[{ required: true, message: 'Vui lòng nhập dung tích tối đa' }]}
                                    >
                                        <Input placeholder="Ví dụ: 25g" />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item
                                        name="grind_setting_count"
                                        label="Số cấp độ xay (cấp)"
                                        rules={[{ required: true, message: 'Vui lòng nhập số cấp độ xay' }]}
                                    >
                                        <Input placeholder="Ví dụ: 28" />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item
                                        name="burr_diameter"
                                        label="Đường kính lưỡi xay (mm)"
                                        rules={[{ required: true, message: 'Vui lòng nhập đường kính lưỡi xay' }]}
                                    >
                                        <Input placeholder="Ví dụ: 38mm" />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item
                                        name="charging_time"
                                        label="Thời gian sạc (h)"
                                        rules={[{ required: true, message: 'Vui lòng nhập thời gian sạc' }]}
                                    >
                                        <Input placeholder="Ví dụ: 1h" />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item
                                        name="manufacture"
                                        label="Nhà sản xuất (hãng)"
                                        rules={[{ required: true, message: 'Vui lòng nhập nhà sản xuất' }]}
                                    >
                                        <Input placeholder="Ví dụ: outln" />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        name="material"
                                        label="Vật liệu (vật liệu)"
                                        rules={[{ required: true, message: 'Vui lòng nhập vật liệu' }]}
                                    >
                                        <Input placeholder="Ví dụ: food grade plastic, aluminum, silicone" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="color"
                                        label="Màu sắc (màu)"
                                        rules={[{ required: true, message: 'Vui lòng nhập màu sắc' }]}
                                    >
                                        <Input placeholder="Ví dụ: white, brown" />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Form.Item
                                name="special_feature"
                                label="Tính năng đặc biệt (tính năng)"
                                rules={[{ required: true, message: 'Vui lòng nhập tính năng đặc biệt' }]}
                            >
                                <Input placeholder="Ví dụ: Auto-stop, Clog-Protection, Fast-Charging, Quiet Operation (<60 dB)" />
                            </Form.Item>

                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        name="price"
                                        label="Giá (VND)"
                                        rules={[{ required: true, message: 'Vui lòng nhập giá' }]}
                                    >
                                        <InputNumber
                                            min={0}
                                            placeholder="4590000"
                                            style={{ width: '100%' }}
                                            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="status"
                                        label="Trạng thái"
                                    >
                                        <Select placeholder="Chọn trạng thái">
                                            <Select.Option value="active">Hoạt động</Select.Option>
                                            <Select.Option value="inactive">Ngừng hoạt động</Select.Option>
                                            <Select.Option value="maintenance">Bảo trì</Select.Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>

                            {/* Upload hình ảnh */}
                            <Form.Item label="Hình ảnh sản phẩm">
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                    <Space>
                                        <Button onClick={() => document.getElementById('grinderFileInput')?.click()}>
                                            Chọn ảnh từ local
                                        </Button>
                                        <Button onClick={handleOpenPicker}>
                                            Chọn ảnh từ Google Drive
                                        </Button>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            multiple
                                            onChange={handleImagesChange}
                                            id="grinderFileInput"
                                            style={{ display: 'none' }}
                                        />
                                    </Space>

                                    {/* Upload progress indicator */}
                                    {uploadProgress > 0 && uploadProgress < 100 && (
                                        <div style={{ marginTop: 8 }}>
                                            <div>Đang tải ảnh lên: {uploadProgress}%</div>
                                            <div style={{ width: '100%', backgroundColor: '#f0f0f0', borderRadius: 4 }}>
                                                <div
                                                    style={{
                                                        width: `${uploadProgress}%`,
                                                        backgroundColor: '#1890ff',
                                                        height: 8,
                                                        borderRadius: 4,
                                                        transition: 'width 0.3s ease'
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {/* Local Files Preview */}
                                    {imageFiles.length > 0 && (
                                        <div style={{ marginTop: 8 }}>
                                            <div>Ảnh từ local:</div>
                                            <Space wrap>
                                                {imageFiles.map((file, index) => (
                                                    <Tag
                                                        key={index}
                                                        color="blue"
                                                        closable
                                                        onClose={() => handleRemoveLocalImage(index)}
                                                        style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}
                                                    >
                                                        <Space>
                                                            File local
                                                            <Image
                                                                src={URL.createObjectURL(file)}
                                                                alt={file.name}
                                                                style={{ width: 30, height: 30, objectFit: 'cover' }}
                                                            />
                                                        </Space>
                                                    </Tag>
                                                ))}
                                            </Space>
                                        </div>
                                    )}

                                    {/* Drive Images Preview */}
                                    {driveImages.length > 0 && (
                                        <div style={{ marginTop: 8 }}>
                                            <div>Ảnh từ Drive:</div>
                                            <Space wrap>
                                                {driveImages.map((image, index) => (
                                                    <Tag
                                                        key={index}
                                                        color="green"
                                                        closable
                                                        onClose={() => handleRemoveDriveImage(index)}
                                                        style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}
                                                    >
                                                        <Space>
                                                            Google Drive
                                                            <Image
                                                                src={`https://lh3.googleusercontent.com/d/${image.fileId}?authuser=server`}
                                                                alt={image.fileName}
                                                                style={{ width: 30, height: 30, objectFit: 'cover' }}
                                                            />
                                                        </Space>
                                                    </Tag>
                                                ))}
                                            </Space>
                                        </div>
                                    )}

                                    {/* Existing Images Preview */}
                                    {((imageFilesPreview && imageFilesPreview.length > 0) ||
                                        (driveImagesPreview && driveImagesPreview.length > 0)) && (
                                            <div>
                                                <div>Ảnh hiện tại:</div>

                                                {imageFilesPreview && imageFilesPreview.length > 0 && (
                                                    <Space wrap>
                                                        {imageFilesPreview.map((file, index) => (
                                                            <Tag
                                                                key={index}
                                                                color="orange"
                                                                closable
                                                                onClose={() => handleRemoveLocalPreviewImage(index)}
                                                                style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}
                                                            >
                                                                <Space>
                                                                    File hiện tại
                                                                    <Image
                                                                        src={file}
                                                                        alt={`file-${index}`}
                                                                        style={{ width: 30, height: 30, objectFit: 'cover' }}
                                                                    />
                                                                </Space>
                                                            </Tag>
                                                        ))}
                                                    </Space>
                                                )}

                                                {driveImagesPreview && driveImagesPreview.length > 0 && (
                                                    <Space wrap>
                                                        {driveImagesPreview.map((image, index) => (
                                                            <Tag
                                                                key={index}
                                                                color="purple"
                                                                closable
                                                                onClose={() => handleRemoveDrivePreviewImage(index)}
                                                                style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}
                                                            >
                                                                <Space>
                                                                    Drive hiện tại
                                                                    <Image
                                                                        src={`https://lh3.googleusercontent.com/d/${image.fileId}?authuser=server`}
                                                                        alt={image.fileName}
                                                                        style={{ width: 30, height: 30, objectFit: 'cover' }}
                                                                    />
                                                                </Space>
                                                            </Tag>
                                                        ))}
                                                    </Space>
                                                )}
                                            </div>
                                        )}
                                </div>
                            </Form.Item>

                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item
                                        name="purchase_date"
                                        label="Ngày mua"
                                    >
                                        <DatePicker
                                            placeholder="Chọn ngày mua"
                                            style={{ width: '100%' }}
                                            format="DD/MM/YYYY"
                                            disabledDate={(current) => {
                                                return current && current > dayjs().endOf('day');
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item
                                        name="warranty_expiry"
                                        label="Hết hạn bảo hành"
                                    >
                                        <DatePicker
                                            placeholder="Chọn ngày hết hạn bảo hành"
                                            style={{ width: '100%' }}
                                            format="DD/MM/YYYY"
                                            disabledDate={(current) => {
                                                return current && current < dayjs().startOf('day');
                                            }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item
                                        name="location"
                                        label="Vị trí"
                                    >
                                        <Input placeholder="Nhập vị trí đặt máy" />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Form.Item
                                name="notes"
                                label="Ghi chú"
                            >
                                <Input.TextArea
                                    rows={3}
                                    placeholder="Nhập ghi chú về máy xay..."
                                />
                            </Form.Item>

                            <Form.Item>
                                <Space>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        className="admin-button"
                                        loading={submitLoading}
                                        disabled={submitLoading}
                                    >
                                        {editingGrinder ? 'Cập nhật' : 'Thêm mới'}
                                    </Button>
                                    <Button
                                        onClick={() => setIsModalVisible(false)}
                                        disabled={submitLoading}
                                    >
                                        Hủy
                                    </Button>
                                </Space>
                            </Form.Item>
                        </Form>
                    </div>
                </Modal>

                <Tour open={open} onClose={() => setOpen(false)} steps={steps} />
            </AdminLayout>
        </ConfigProvider>
    );
};

export default GrinderManagement;